import { WorkareaPricingData } from '../../../domain/workareas/workareaPricing'
import { WorkareaSelectedPricing } from '../../../domain/workareas/workareaPricingApi'
import { ItemClass, ItemClasses, PricingType, PricingTypes } from "../../../common/constants"
import { CompletedWorkareaVisit } from "../../../domain/invoicing/invoicingApi"
import { ActualItem, EstimatedItem, WorkareaItemBase, isActualItem, isEstimatedItem } from "../../../domain/workareaItem"
import { sum } from "../../../helpers/array"
import { roundToPrecision } from "../../../helpers/math"
import { hasValue } from "../../../helpers/nullable"
import { ItemWorkareaData, ChangeSet, calculateTotalStandardPrice, getUpdatedItem } from "../itemModel"

type AmountAndPercentage = {
  amount: number
  percentage: number
}

type ProfitData = {
  estimated: AmountAndPercentage
  final: AmountAndPercentage
}

type ClassCosts = {
  labor: number
  materials: number
  equipment: number
  subcontracting: number
  laborHours?: number
}

type ExtendedClassCosts = ClassCosts & {
  overhead: number
  breakeven: number
}

export type JobFinancialsData = {
  price: {
    estimated: number
    actual: number
    final: number
  }
  grossProfit: ProfitData
  netProfit: ProfitData
  estimated: ExtendedClassCosts,
  actual: ExtendedClassCosts,
  unbillable: ClassCosts,
  jobs: {
    number: string,
    name: string,
    url: string
  }[]
}

export type JobCostingWorkarea = {
  pricing?: WorkareaPricingData | null
  recovery?: number
  id: number
  quotedPrice?: number
  isOnHold: boolean
  estimatedOccurrences?: number
  pricingType: PricingType
}

type Totals = {
  totalPrice: number
  costPlusTax: number
  breakeven: number
}

type WorkareaTotals = JobCostingWorkarea & {
  estimated: Totals
  actual: Totals
  estimatedOccurrences: number
}

type JobFinancialsInput = {
  estimatedItems: EstimatedItem[]
  actualItems: ActualItem[]
  workareas: JobCostingWorkarea[]
}

const shouldUseQuotedPrice = (workarea: JobCostingWorkarea) => {
  return workarea.pricingType === PricingTypes.QuotedPrice || workarea.pricingType === PricingTypes.PerVisit
}

export const calculateJobFinancialsData = (input: JobFinancialsInput, completedWorkareaVisits: CompletedWorkareaVisit[], selectedPricing: WorkareaSelectedPricing[] = []): JobFinancialsData => {
  let estimatedItemsTotalPrice = 0
  let actualItemsTotalStandardPrice = 0
  const estimatedCosts = createExtendedClassCosts()
  const actualCosts = createExtendedClassCosts()
  const unbillableCosts = createClassCosts()

  const allWorkareaTotals: WorkareaTotals[] = input.workareas.map(x => ({
    ...x,
    estimated: { totalPrice: 0, costPlusTax: 0, breakeven: 0 },
    actual: { totalPrice: 0, costPlusTax: 0, breakeven: 0 },
    estimatedOccurrences: x.estimatedOccurrences ?? 1
  }))

  input.estimatedItems.forEach(estimatedItem => {
    const workareaTotals = getWorkareaTotals(allWorkareaTotals, estimatedItem.workareaId)
    estimatedItemsTotalPrice += estimatedItem.totalPrice * workareaTotals.estimatedOccurrences
    mutateBreakdownsAndWorkareaTotals(estimatedCosts, estimatedItem, workareaTotals.estimated, workareaTotals.estimatedOccurrences)
  })

  input.actualItems.forEach(actualItem => {
    const workareaTotals = getWorkareaTotals(allWorkareaTotals, actualItem.workareaId)
    actualItemsTotalStandardPrice += calculateTotalStandardPrice(actualItem)
    mutateBreakdownsAndWorkareaTotals(actualCosts, actualItem, workareaTotals.actual, 1, unbillableCosts)
  })

  // Replace item-derived revenue only where the server provides a pricing breakdown.
  allWorkareaTotals.forEach(x => {
    const selected = selectedPricing.find(p => p.workareaId === x.id)
    if (x.pricing) {
      const count = x.pricingType === PricingTypes.PerVisit ? x.estimatedOccurrences : 1
      estimatedItemsTotalPrice += x.pricing.calculatedEstimate.customerTotal * count - x.estimated.totalPrice
    }
    if (selected) {
      actualItemsTotalStandardPrice += selected.actual.customerTotal
        - sum(input.actualItems.filter(item => item.workareaId === x.id), calculateTotalStandardPrice)
    }
  })

  const estimatedPrice = sum(allWorkareaTotals, x => {
    if (x.pricing) {
      return x.pricing.estimated.customerTotal * (x.pricingType === PricingTypes.PerVisit ? x.estimatedOccurrences : 1)
    }
    if (x.pricingType === PricingTypes.PerVisit) {
      return (x.quotedPrice ?? x.estimated.totalPrice) * x.estimatedOccurrences
    }
    return shouldUseQuotedPrice(x) ? x.quotedPrice ?? x.estimated.totalPrice : x.estimated.totalPrice
  })
  const actualPrice = sum(allWorkareaTotals, x => {
    const selected = selectedPricing.find(p => p.workareaId === x.id)
    if (x.pricingType === PricingTypes.PerVisit) {
      return selected?.completedVisits?.customerTotal ?? sum(completedWorkareaVisits.filter(y => y.workareaId === x.id), y => y.visitPrice ?? 0)
    }
    if (shouldUseQuotedPrice(x) && hasValue(x.quotedPrice)) {
      return x.quotedPrice
    }
    return selected?.actual.customerTotal ?? x.actual.totalPrice
  })

  const estimatedRecovery = sum(allWorkareaTotals, x =>
    (x.pricing?.estimated.recovery ?? x.recovery ?? 0) * (x.pricingType === PricingTypes.PerVisit ? x.estimatedOccurrences : 1))
  const actualRecovery = sum(allWorkareaTotals, x => {
    const selected = selectedPricing.find(p => p.workareaId === x.id)
    if (x.pricingType === PricingTypes.PerVisit) {
      return selected?.completedVisits?.recovery ?? sum(completedWorkareaVisits.filter(v => v.workareaId === x.id), v => v.visitRecovery ?? 0)
    }
    return shouldUseQuotedPrice(x) && hasValue(x.quotedPrice) ? x.recovery ?? 0 : selected?.actual.recovery ?? 0
  })
  const estimatedProfit = calculateNetAndGrossProfit(estimatedPrice - estimatedRecovery, allWorkareaTotals.map(x => x.estimated))
  const actualProfit = calculateNetAndGrossProfit(actualPrice - actualRecovery, allWorkareaTotals.map(x => x.actual))

  return {
    price: {
      estimated: estimatedItemsTotalPrice,
      actual: actualItemsTotalStandardPrice,
      final: actualPrice
    },
    netProfit: { estimated: estimatedProfit.netProfit, final: actualProfit.netProfit },
    grossProfit: { estimated: estimatedProfit.grossProfit, final: actualProfit.grossProfit },
    estimated: estimatedCosts,
    actual: actualCosts,
    unbillable: unbillableCosts,
    jobs: []
  }
}

const getWorkareaTotals = (allWorkareaTotals: WorkareaTotals[], workareaId: number): WorkareaTotals => {
  const workareaTotals = allWorkareaTotals.find(x => x.id === workareaId)
  if (!workareaTotals) {
    throw new Error(`Unable to find workarea totals for workareaId ${workareaId}`)
  }
  return workareaTotals
}

const mutateBreakdownsAndWorkareaTotals = (costs: ExtendedClassCosts, item: WorkareaItemBase, totals: Totals, estimatedOccurrences: number, unbillableCosts?: ClassCosts) => {
  const totalOverhead = item.overhead * item.quantity
  const costPlusTax = item.totalBreakeven - totalOverhead

  totals.totalPrice += item.totalPrice * estimatedOccurrences
  totals.costPlusTax += costPlusTax * estimatedOccurrences
  totals.breakeven += item.totalBreakeven * estimatedOccurrences

  mutateClassCosts(costs, item.class, costPlusTax * estimatedOccurrences)

  if (item.isBillable) {
    costs.overhead += totalOverhead * estimatedOccurrences
    costs.breakeven += item.totalBreakeven * estimatedOccurrences
  } else if (unbillableCosts) {
    mutateClassCosts(unbillableCosts, item.class, costPlusTax * estimatedOccurrences)
  }
}

const mutateClassCosts = (data: ClassCosts, itemClass: ItemClass, add: number) => {
  switch (itemClass) {
    case ItemClasses.Labor:
      data.labor += add
      break;
    case ItemClasses.Materials:
      data.materials += add
      break;
    case ItemClasses.Equipment:
      data.equipment += add
      break;
    case ItemClasses.Subcontracting:
      data.subcontracting += add
      break;
  }
}

const calculateNetAndGrossProfit = (price: number, data: Totals[]): { grossProfit: AmountAndPercentage, netProfit: AmountAndPercentage } => {
  const grossAmount = price - sum(data, x => x.costPlusTax)
  const netAmount = price - sum(data, x => x.breakeven)
  return {
    grossProfit: { amount: grossAmount, percentage: calculateProfitPercentage(grossAmount, price) },
    netProfit: { amount: netAmount, percentage: calculateProfitPercentage(netAmount, price) }
  }
}

const calculateProfitPercentage = (amount: number, price: number): number => {
  if (price !== 0) {
    return roundToPrecision(amount / price * 100, 2)
  }
  return 0
}

const createClassCosts = (): ClassCosts => ({
  labor: 0,
  materials: 0,
  equipment: 0,
  subcontracting: 0
})

const createExtendedClassCosts = (): ExtendedClassCosts => ({
  ...createClassCosts(),
  overhead: 0,
  breakeven: 0
})

type FilterInput = {
  estimatedItems: EstimatedItem[]
  actualItems: ActualItem[]
  workareas: JobCostingWorkarea[]
  changeSet: ChangeSet
  enabledWorkareaIds: number[]
  visitFilters: VisitFilter[]
}

type FilterOutput = {
  estimatedItems: EstimatedItem[]
  actualItems: ActualItem[],
  workareas: JobCostingWorkarea[]
}

export type VisitFilter = {
  compoundId: string
  include: boolean
}

export const filterItems = (input: FilterInput): FilterOutput => {
  const workareas = input.workareas
    .filter(x => input.enabledWorkareaIds.includes(x.id) && !x.isOnHold)

  const estimatedItems = input.estimatedItems
    .concat(input.changeSet.addedItems.filter(x => isEstimatedItem(x)) as EstimatedItem[])
    .filter(x => filterByWorkareaId(workareas, x))
    .map(x => getUpdatedItem(x, input.changeSet))
    .filter(x => !x.isDeleted)
    .map(x => x.item)

  const actualItems = input.actualItems
    .concat(input.changeSet.addedItems.filter(x => isActualItem(x)) as ActualItem[])
    .filter(x => filterByWorkareaId(workareas, x))
    .filter(x => filterByVisit(input.visitFilters, x))
    .map(x => getUpdatedItem(x, input.changeSet))
    .filter(x => !x.isDeleted)
    .map(x => x.item as ActualItem)

  return {
    estimatedItems,
    actualItems,
    workareas
  }
}

const filterByWorkareaId = (workareas: JobCostingWorkarea[], item: ActualItem | EstimatedItem): boolean => {
  return workareas.some(x => x.id === item.workareaId)
}

const filterByVisit = (visitFilters: VisitFilter[], item: ActualItem): boolean => {
  if (!hasValue(item.visitId)) {
    return true
  }

  const compoundId = `${item.visitId}__${item.workareaId}`
  const matchingFilter = visitFilters.find(x => x.compoundId === compoundId)
  if (!matchingFilter) {
    return true
  }

  return matchingFilter.include
}

export const mergeWorkareaContext = (workareas: JobCostingWorkarea[], context: ItemWorkareaData[]): JobCostingWorkarea[] =>
  workareas.map(workarea => {
    const saved = context.find(x => x.id === workarea.id)
    if (!saved) {
      return workarea
    }
    return { ...workarea, pricing: saved.pricing === undefined ? workarea.pricing : saved.pricing,
      recovery: saved.recovery ?? workarea.recovery, quotedPrice: saved.quotedPrice ?? workarea.quotedPrice }
  })

export const buildJobCostingPricingSelections = (filtered: JobFinancialsInput, visitFilters: VisitFilter[],
  visits: { id: string }[], completedVisits: { visitId: string }[]) => filtered.workareas.map(workarea => ({
    workareaIds: [workarea.id],
    actualItemIds: filtered.actualItems.filter(x => x.workareaId === workarea.id).map(x => x.guid).sort(),
    visitIds: visitFilters.some(x => !x.include)
      ? Array.from(new Set([...visits.map(x => x.id), ...completedVisits.map(x => x.visitId)]))
        .filter(id => !visitFilters.some(x => x.compoundId === `${id}__${workarea.id}` && !x.include)).sort()
      : undefined,
  }))
